import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import RequesterName from '../RequesterName';
import { deviceName } from '../../lib/assets';
import { stamp, hrs, euro, whenWords, slaFact } from './ticketView';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * THE FACTS, beside the conversation on a desktop and a tab of its own on a
 * phone. Read-only for the requester and the contractor. For an
 * administrator each value is a button: click it, pick, and it is saved with
 * the same PUT /tickets/:id the old six-dropdown edit panel used - one field
 * at a time, so changing the priority is two clicks rather than a form.
 *
 * Under it, "At a glance": hours against the budget, what has been spent,
 * the next visit, the work order and the files - each a link to the tab it
 * summarises. Only what this person's own tabs would show them.
 */
const FIELDS = [
  { key: 'status_id', label: 'Status', shown: (t) => t.status_name, list: 'statuses' },
  { key: 'priority_id', label: 'Priority', shown: (t) => t.priority_name, list: 'priorities' },
  { key: 'category_id', label: 'Category', shown: (t) => t.category_name, list: 'categories' },
  { key: 'department_id', label: 'Department', shown: (t) => t.department_name, list: 'departments' },
  { key: 'assigned_to', label: 'Assigned to', shown: (t) => t.assigned_to_name, list: 'users', none: 'Nobody' },
  { key: 'contractor_id', label: 'Contractor', shown: (t) => t.contractor_name, list: 'contractors', none: 'Nobody' }
];

let reference = null;  // shared across the page: the lists only change in Settings

async function loadReference() {
  if (reference) return reference;
  const [refRes, conRes] = await Promise.all([
    axios.get(`${API_BASE}/reference-data`),
    axios.get(`${API_BASE}/contractors`).catch(() => ({ data: [] }))
  ]);
  const d = refRes.data || {};
  const arr = (x) => (Array.isArray(x) ? x : []);
  const cons = Array.isArray(conRes.data) ? conRes.data : (conRes.data?.contractors || []);
  reference = {
    statuses: arr(d.statuses),
    priorities: arr(d.priorities),
    categories: arr(d.categories),
    departments: arr(d.departments),
    users: arr(d.users).map((u) => ({ id: u.id, name: u.display_name || u.name || u.email })),
    contractors: cons.filter((c) => c.availability_status !== 'inactive').map((c) => ({ id: c.id, name: c.name }))
  };
  return reference;
}

export default function DetailsPanel({ ctx, asTab = false }) {
  const { ticket, summary, role, can, base, reload, setNotice, setProblem, editField, setEditField } = ctx;
  const sla = can?.seesSla ? slaFact(ctx.sla) : null;
  const plain = role === 'requester' || role === 'raiser';
  const [editing, setEditing] = useState(null);
  const [options, setOptions] = useState(null);
  const [saving, setSaving] = useState(false);
  const select = useRef(null);

  // A shortcut (s, a) asks for a field from the header.
  useEffect(() => {
    if (!editField || !can.canEdit) return;
    // Only the visible copy answers: the side panel on a desktop, the tab on
    // a phone.
    const desktop = typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 1024px)').matches;
    if (desktop === asTab) return;
    setEditing(editField);
    setEditField(null);
  }, [editField, can.canEdit, asTab, setEditField]);

  useEffect(() => {
    if (!editing) return;
    let live = true;
    loadReference()
      .then((r) => { if (live) setOptions(r); })
      .catch(() => { if (live) { setOptions(null); setProblem('Could not load the choices for that field.'); setEditing(null); } });
    return () => { live = false; };
  }, [editing, setProblem]);

  useEffect(() => { if (editing && options) select.current?.focus(); }, [editing, options]);

  const save = async (field, raw) => {
    const value = raw === '' ? null : raw;
    if (String(ticket[field] ?? '') === String(value ?? '')) { setEditing(null); return; }
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/tickets/${ticket.id}`, { [field]: value });
      setNotice('Saved.');
      setEditing(null);
      await reload();
    } catch (err) {
      setProblem(err.response?.data?.error || 'Could not save that change.');
    } finally {
      setSaving(false);
    }
  };

  const row = (f) => {
    const value = f.shown(ticket);
    if (!can.canEdit) {
      if (!value) return null;
      return (
        <div className="tk-fact" key={f.key}>
          <dt>{f.label}</dt><dd>{value}</dd>
        </div>
      );
    }
    if (editing === f.key) {
      const list = options?.[f.list] || [];
      return (
        <div className="tk-fact tk-fact-editing" key={f.key}>
          <dt><label htmlFor={`tk-edit-${f.key}`}>{f.label}</label></dt>
          <dd>
            {!options ? <span className="tk-quiet">Loading…</span> : (
              <select id={`tk-edit-${f.key}`} ref={select} className="tk-input tk-select"
                      defaultValue={ticket[f.key] ?? ''} disabled={saving}
                      onChange={(e) => save(f.key, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setEditing(null); } }}
                      onBlur={() => { if (!saving) setEditing(null); }}>
                {f.none ? <option value="">{f.none}</option> : !ticket[f.key] && <option value="">Choose…</option>}
                {list.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            )}
          </dd>
        </div>
      );
    }
    return (
      <div className="tk-fact" key={f.key}>
        <dt>{f.label}</dt>
        <dd>
          <button type="button" className="tk-editable" data-field={f.key}
                  aria-label={`${f.label}: ${value || f.none || 'not set'}. Change`}
                  onClick={() => setEditing(f.key)}>
            <span>{value || <span className="tk-quiet">{f.none || 'Not set'}</span>}</span>
            <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" fill="none" stroke="currentColor"
                 strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.5 2.5l3 3L6 13H3v-3z" />
            </svg>
          </button>
        </dd>
      </div>
    );
  };

  // The requester's facts are plainer: no category plumbing, no contractor
  // internals - who has it, and when it was raised.
  const fields = plain
    ? FIELDS.filter((f) => ['status_id', 'priority_id', 'assigned_to', 'contractor_id'].includes(f.key))
    : FIELDS;

  const work = summary?.work || null;
  const exp = summary?.expenses || null;
  const visit = summary?.next_visit || null;
  const files = summary?.files;
  const tabs = new Set((ctx.role && ['admin', 'worker', 'contractor'].includes(ctx.role)) ? ['work'] : []);
  const over = work && work.allocated_hours > 0 && work.logged_hours > work.allocated_hours;
  const pct = work && work.allocated_hours > 0 ? Math.min(100, (work.logged_hours / work.allocated_hours) * 100) : 0;

  return (
    <div className={`tk-details${asTab ? ' tk-details-tab' : ''}`}>
      <section className="tk-card tk-facts-card" aria-labelledby={asTab ? 'tk-details-h-tab' : 'tk-details-h'}>
        <h2 className="tk-card-title" id={asTab ? 'tk-details-h-tab' : 'tk-details-h'}>
          Details{can.canEdit && <span className="tk-card-sub"> · click a value to change it</span>}
        </h2>
        <dl className="tk-facts">
          {fields.map(row)}
          {ticket.requester_name && (
            <div className="tk-fact"><dt>Raised by</dt><dd><RequesterName ticket={ticket} /></dd></div>
          )}
          {role === 'contractor' && ticket.requester_email && (
            <div className="tk-fact"><dt>Contact</dt><dd><a href={`mailto:${ticket.requester_email}`}>{ticket.requester_email}</a></dd></div>
          )}
          {ticket.approver_name && (
            <div className="tk-fact"><dt>Approver</dt><dd>{ticket.approver_name}</dd></div>
          )}
          {ticket.asset && !plain && (
            <div className="tk-fact">
              <dt>Device</dt>
              <dd><Link to={`${base}/device`}>{ticket.asset.asset_tag || deviceName(ticket.asset)}</Link></dd>
            </div>
          )}
          {ticket.related_ticket_id && ticket.related_ticket_number && (
            <div className="tk-fact">
              <dt>About</dt>
              <dd><Link to={ctx.contractorMode ? `/my-tickets/${ticket.related_ticket_id}` : `/tickets/${ticket.related_ticket_id}`}>
                {ticket.related_ticket_number}</Link></dd>
            </div>
          )}
          <div className="tk-fact"><dt>Opened</dt><dd>{stamp(ticket.created_at)}</dd></div>
          {ticket.resolved_at && <div className="tk-fact"><dt>Resolved</dt><dd>{stamp(ticket.resolved_at)}</dd></div>}
          {/* Which working hours its SLA runs on, and where it stands in them (114). */}
          {sla && (
            <div className="tk-fact" data-fact="sla">
              <dt>SLA hours</dt>
              <dd title={sla.summary || undefined}>
                <span className="tk-sla-name">{sla.name}</span>
                {sla.why && <span className="tk-quiet"> · {sla.why}</span>}
                {sla.summary && sla.summary !== sla.name && <span className="tk-sla-summary">{sla.summary}</span>}
                {sla.state && <span className="tk-sla-state">{sla.state}</span>}
              </dd>
            </div>
          )}
        </dl>
      </section>

      {plain ? (
        visit && (
          <section className="tk-card" aria-label="Coming up">
            <h2 className="tk-card-title">Coming up</h2>
            <p className="tk-coming">Visit · {whenWords(visit.starts_at)}</p>
            {(visit.location || visit.created_by) && (
              <p className="tk-quiet tk-small">{[visit.location, visit.created_by].filter(Boolean).join(' · ')}</p>
            )}
          </section>
        )
      ) : (
        <section className="tk-card" aria-label="At a glance">
          <h2 className="tk-card-title">At a glance</h2>
          <div className="tk-tiles">
            {tabs.has('work') && work && (
              <Link className="tk-tile" to={`${base}/work`} data-tile="hours" data-over={over || undefined}>
                <span className="tk-tile-label">Hours{work.allocated_hours > 0 ? ' / budget' : ''}</span>
                <span className="tk-tile-value">
                  {hrs(work.logged_hours)}{work.allocated_hours > 0 ? ` / ${hrs(work.allocated_hours)}` : ''}
                </span>
                {work.allocated_hours > 0 && (
                  <span className="tk-bar" aria-hidden="true"><span style={{ width: `${pct}%` }} /></span>
                )}
              </Link>
            )}
            {tabs.has('work') && exp && (
              <Link className="tk-tile" to={`${base}/work`} data-tile="expenses">
                <span className="tk-tile-label">Expenses</span>
                <span className="tk-tile-value">{exp.count ? euro(exp.total) : 'None'}</span>
              </Link>
            )}
            <Link className="tk-tile" to={tabs.has('work') ? `${base}/work` : base} data-tile="visit">
              <span className="tk-tile-label">Next visit</span>
              <span className="tk-tile-value">{visit ? whenWords(visit.starts_at) : 'None booked'}</span>
            </Link>
            {tabs.has('work') && work && (
              <Link className="tk-tile" to={`${base}/work`} data-tile="work-order">
                <span className="tk-tile-label">Work order</span>
                <span className="tk-tile-value tk-mono">{work.work_order?.number || 'None yet'}</span>
              </Link>
            )}
            <Link className="tk-tile" to={`${base}/files`} data-tile="files">
              <span className="tk-tile-label">Files</span>
              <span className="tk-tile-value">{files ?? '—'}</span>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
